import { AddOutlined } from '@mui/icons-material';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DocumentTemplate from '../../documentTemplate/containers';
import OrganizationSelector from '../../profile/components/organizationSelector';
import DocumentTable from '../components/documentTable';
import DocumentPreview from 'components/v2/common/documentPreview';
import PageHeader from 'components/v2/common/pageHeader';

import api from 'api';
import PERMISSION from 'assets/permissions.json';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import { organizationDocumentKeys, useOrganizationDocuments } from 'hooks/useOrganizationDocuments';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import { DocumentInterface } from 'types';
import { downloadBlob, getFileBlob } from 'utils/file';
import { getExplanationUrl } from 'utils/v2/document';

interface DocumentPreviewModalData {
    fileUrl: string;
    fileName: string;
}

const OrganizationDocuments = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const { id } = useParams();
    const queryClient = useQueryClient();
    const { data: documents = [], isLoading } = useOrganizationDocuments(id, {
        alertOnError: true,
    });
    const [uploadingDocumentId, setUploadingDocumentId] = useState<number | null>(null);
    const { modalObject, setModalObject, onResetModalObj } = useModalObj();
    const canRequestOrganizationDocuments = permissionsList.some(
        (permission) => permission?.name === PERMISSION.REQUEST_ORGANIZATION_DOCUMENTS
    );

    const refreshDocuments = async () => {
        if (!id) return;
        await queryClient.invalidateQueries({ queryKey: organizationDocumentKeys.list(id) });
    };

    const handlePreviewUploadedDoc = async (data?: DocumentInterface) => {
        try {
            if (!id || !data) {
                return;
            }

            setModalObject((prev) => ({ ...prev, disabled: true }));
            const response = await api.clientServiceProviders.getDocumentPreviewUrl(id, data.id);
            const url = response.data.url;

            setModalObject((prev) => ({
                ...prev,
                action: ACTIONS.PREVIEW_FILE,
                title: 'templatePreview',
                data: {
                    fileUrl: url,
                    fileName: data.document_name,
                },
            }));
        } catch {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const handleFilePreview = async (data?: DocumentInterface, isPreview = false) => {
        if (!id || !data) {
            return;
        }

        // "Template from URL" docs have no uploaded file — open the linked template directly.
        if (isPreview && !data.template_uri) {
            const url = getExplanationUrl(data, language);
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
        }

        try {
            setModalObject((prev) => ({ ...prev, disabled: true }));
            let response;
            if (isPreview) {
                response = await api.clientServiceProviders.getDocumentTemplateUrl(id, data.id);
            } else {
                response = await api.clientServiceProviders.getDocumentSampleUrl(id, data.id);
            }
            const url = response.data.url;

            setModalObject((prev) => ({
                ...prev,
                action: ACTIONS.PREVIEW_FILE,
                title: 'templatePreview',
                data: {
                    fileUrl: url,
                    fileName: data.document_name,
                },
            }));
        } catch {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const handleClose = () => {
        void refreshDocuments();
        onResetModalObj();
    };

    const handleDownload = async (data?: DocumentInterface) => {
        if (!id || !data?.resource_name) {
            return;
        }

        try {
            const response = await api.clientServiceProviders.getDocumentPreviewUrl(id, data.id);
            const url = response.data?.url;
            if (typeof url !== 'string' || !url) {
                dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
                return;
            }

            const blob = await getFileBlob(url);
            downloadBlob(blob, data.document_name || 'document');
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const handleUpload = async (data: DocumentInterface, file: File) => {
        try {
            if (!id) {
                return;
            }

            setUploadingDocumentId(data.id);

            const payload = new FormData();
            payload.append('document_template_id', String(data.document_template_id));
            payload.append('document', file);

            await api.clientServiceProviders.uploadDocument(id, data.id, payload);
            await refreshDocuments();
            dispatch(setAlert({ message: 'saveChanges', type: 'success' }));
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        } finally {
            setUploadingDocumentId(null);
        }
    };

    const handleAction = (action: ACTIONS, data?: DocumentInterface) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'addDocumentTemplate',
                    data: null,
                }));
                break;
            case ACTIONS.DOWNLOAD:
                void handleDownload(data);
                break;
            case ACTIONS.SELECT:
                void handlePreviewUploadedDoc(data);
                break;
            case ACTIONS.PREVIEW_FILE:
                handleFilePreview(data as DocumentInterface, true);
                break;
            case ACTIONS.PREVIEW_SAMPLE:
                handleFilePreview(data as DocumentInterface, false);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <PageHeader title="documents">
                    <OrganizationSelector />
                    {canRequestOrganizationDocuments ? (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddOutlined />}
                            onClick={() => handleAction(ACTIONS.CREATE)}
                        >
                            {translate('addDocument')}
                        </Button>
                    ) : null}
                </PageHeader>

                <DocumentTable
                    loading={isLoading}
                    uploadingDocumentId={uploadingDocumentId}
                    documents={documents}
                    handleAction={handleAction}
                    handleUploadFile={handleUpload}
                    getTemplateUrl={(doc) => getExplanationUrl(doc, language)}
                />
            </Box>

            {modalObject.action === ACTIONS.CREATE ? (
                <DocumentTemplate documents={documents} onClose={handleClose} />
            ) : null}

            {(modalObject.action === ACTIONS.PREVIEW_FILE ||
                modalObject.action === ACTIONS.PREVIEW_SAMPLE) && (
                <DocumentPreview
                    open
                    mobile={mobile}
                    onClose={onResetModalObj}
                    title={translate(modalObject.title)}
                    fileUrl={(modalObject.data as DocumentPreviewModalData | null)?.fileUrl}
                    fileName={(modalObject.data as DocumentPreviewModalData | null)?.fileName}
                />
            )}
        </>
    );
};

export default OrganizationDocuments;
